import React, { useMemo } from "react";

import { ProgressBar } from "react-bootstrap";
import pluralize from "pluralize";
import moment from "moment";
import dateformat from "dateformat";

import { FileService } from "src/utils/file.service";
import Text from "src/components/text";

import { Media } from "src/types/media";
import Thumbnail from "src/components/thumbnail";
import { LearningRecordSummary } from "src/types/coaching/learners";
import { Course } from "src/types/coaching/courses";

import _ from "lodash";
import cx from "classnames";
import styles from "./card.module.scss";
import { Simulation } from "src/types/coaching/simulation";
import { Podcast } from "src/types/coaching/podcast";
import { CALL_TYPES } from "src/constants/simulation";

interface CourseCardProps {
  course: LearningRecordSummary;
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";
  onClick: (course: LearningRecordSummary) => void;
}

const defaultCourse = "/images/assets/courseThumbnail.jpg";
const defaultPodcast = "/images/assets/podcastThumbnail.jpg"
const defaultSimulation = "/images/assets/simulationThumbnail.jpg"
const defaultImage = "/images/collections/background.jpeg";

interface MyCourseCardProps {
  course: Course;
  onClick: (course: any) => void;
  status?: String;
}

export const MyCourseCard: React.FC<MyCourseCardProps> = ({
  course,
  onClick,
}) => {
  const name = useMemo(() => _.get(course, "course.name", ""), [course]);
  const lessons = useMemo(() => _.get(course, "course.lessons", 0), [course]);
  const assessment = useMemo(
    () => _.get(course, "course.assessments", 0),
    [course],
  );
  const duration = useMemo(
    () => _.get(course, "course.duration", 0),
    [course],
  );
  const icon = useMemo(
    () => _.get(course, "course.banner.background", null) as Media | null,
    [course],
  );

  const position = useMemo(
    () => _.get(course, "course.banner.position", "center"),
    [course],
  );

  const crop = useMemo(
    () => _.get(course,"course.banner.crop", null),
    [course],
  );

  const passPercentage = useMemo(
    () => _.get(course, "course.passPercentage", 0),
    [course],
  );

  const completed = useMemo(() => _.get(course, "completedCount", 0),[course])


  const createdDate = useMemo(() => _.get(course, "createdDate"), [course]);

  const createdDateValue = dateformat( createdDate,  "mmm dd, yyyy");
    
  const completedDateValue = useMemo(
    () => _.get(course, "completedDate"),
    [course],
  );
  const completedDate = completedDateValue && new Date(completedDateValue).toLocaleDateString();

  const createdBy = useMemo(
    () => _.get(course, "course.createdBy.icon", null),
    [course],
  );
  const dueDate = _.get(course, "dueDate", null);
  const totalLessons = lessons + assessment;

  const score = useMemo(() => {
    const percentage = Math.round((completed / totalLessons) * 100) ;
    return percentage;
  }, [course]);

  const passScore = useMemo(() => {
    const percentage = Math.round(_.get(course,'score',0)*100);
    return percentage;
  },[course])

  const getDueDate = () => {
    if (!dueDate) return null; 

    const date = moment(dueDate).startOf("day");
    const today = moment().startOf("day");
    const diff = date.diff(today, "days");

    if (diff === 0) {
      return (
        <>
          <i className="paperflite-calendar" />{" "}
          <span className={styles.name}>due today</span>
        </>
      )
    }

    if (diff > 0) {
      return (
      <>
        <i className="paperflite-calendar" />{" "}
        <span className={styles.name}>due {_.lowerCase(date.format("D MMM"))}</span>
      </>
      )
    }

    // diff < 0 → overdue
    const abs = Math.abs(diff);
    if (abs < 7) {
      return (
        <>
          <i className={cx("paperflite-danger",styles.danger)} />
          <span className={styles.name}>overdue by {abs} {pluralize("day", abs)}</span>
        </>
      );
    }
    // For >1 week
    const weeks = Math.floor(abs / 7);
    return (
      <>
        <i className={cx("paperflite-danger",styles.danger)} />
        <span className={styles.name}>overdue by {weeks} {pluralize("week", weeks)}</span>
      </>
    );
  };

  return (
    <div
      className={cx(styles.card, styles.courseCard)}
      onClick={() => onClick(course)}
    >
      <div className={styles.header}>
        <div className={cx(styles.top)}>
          <Thumbnail
            className={styles.icon}
            media={icon}
            defaultImage={defaultCourse}
            width={600}
            position={position}
            crop={crop || undefined}
          />
          <i className={cx("paperflite-book", styles.book)} />
          {dueDate && course.status != "COMPLETED" && (
            <div className={styles.duedate}>
              <>{getDueDate()}</>
            </div>
          )}
        </div>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span className={styles.value}>
              {FileService.formatDuration(duration, "short")}
            </span>
          </div>
          { lessons > 0 && 
          <>
          <div className={styles.divider}></div>
          <div className={styles.metadata}>
            <i className="paperflite-document"></i>
            <span className={styles.value}>
              {lessons} {pluralize("lesson", lessons)}
            </span>
          </div>
          </>
        }
        {
          assessment > 0 &&
          <>
          <div className={styles.divider}></div>
          <div className={styles.metadata}>
            <i className="paperflite-assessment"></i>
            <span className={styles.value}>
              {assessment} {pluralize("assessment", assessment)}
            </span>
          </div>
          </>
        } 
        </div>
        <div className={styles.details}>
          <Text className={styles.name} text={name} lines={2} />
        </div>
      </div>

      <div className={styles.footer}>
        {course.status === "NOT_STARTED" && (
          <div>
            <div className={styles.metadata}>
              <Thumbnail
                className={styles.icon}
                media={createdBy}
                defaultImage={defaultCourse}
                width={18}
              />
              <span className={styles.days} >
                Added on {createdDateValue}
              </span>
            </div>
            <button className={cx(styles.action)}>
              <i className="paperflite-right-arrow" />
              <span>start learning</span>
            </button>
          </div>
        )}
        {course.status === "IN_PROGRESS" && (
          <div>
            <div className={styles.progressmetadata}>
              <div className={styles.progressBar}>
                <ProgressBar now={score} />

                <span> {score || 0}%</span>
              </div>
              <div>
                <span className={styles.lessonCount}> {completed}/ {totalLessons}</span>
              </div>
            </div>
            <button className={cx(styles.action, styles.start)}>
              <i className="paperflite-right-arrow" />
              <span>continue learning</span>
            </button>
          </div>
        )}
        {course.status === "COMPLETED" && (
          <div>
            <div className={styles.metadata}>
              <span>
                score:{" "}
                <span
                  className={
                    passScore < passPercentage ? styles.fail : styles.pass
                  }
                >
                  {" "}
                  {passScore}%{" "}
                </span>{" "}
              </span>
              <div className={styles.divider}></div>
              <div className={styles.completed}>
                <i className="paperflite-calendar" />{" "}
                <span>completed on {completedDate || ""}</span>
              </div>
            </div>
            <button className={cx(styles.action)}>
              <i className="paperflite-right-arrow" />
              <span>view reports</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

interface PodcastCardProps {
  course: Podcast;
  onClick: (course: any) => void;
  status?: String;
}

export const PodcastCard: React.FC<PodcastCardProps> = ({
  course,
  onClick,
}) => {

  const name = useMemo(() => _.get(course, "course.name", ""), [course]);

  const icon = useMemo(
    () => _.get(course, "course.banner.background"),
    [course],
  );

  const position = useMemo(
    () => _.get(course, "course.banner.position", "center"),
    [course],
  );

  const crop = useMemo(
    () => _.get(course,"course.banner.crop", null),
    [course],
  );

  const createdDate = useMemo(() => _.get(course, "createdDate"), [course]);

  const createdDateValue = dateformat(createdDate, "mmm dd, yyyy");

  const score = useMemo(() => {
    const score = _.get(course, "score", 0);
    return (score * 100).toFixed(0);
  }, [course]);

  const passPercentage = useMemo(
    () => _.get(course, "course.passPercentage", 0),
    [course],
  );
  const createdBy = useMemo(
    () => _.get(course, "course.createdBy.icon", null),
    [course],
  );

  const completedDateValue = useMemo(
    () => _.get(course, "completedDate"),
    [course],
  );
  const completedDate = completedDateValue
    ? new Date(completedDateValue).toLocaleDateString()
    : "";

  const podcastType = useMemo(() => _.get(course, "course.type"), [course]);

  const duration = useMemo(() => _.get(course, "course.duration", 0), [course]);
  const dueDate = _.get(course, "dueDate", null);
  const percentCompleted = useMemo(()=>_.get(course, 'percentCompleted', 0),[course]);

  const completedDuration = useMemo(()=>_.get(course,'progressCompleted',0),[course])


  const getDueDate = () => {
    if (!dueDate) return null;

    const date = moment(dueDate).startOf("day");
    const today = moment().startOf("day");
    const diff = date.diff(today, "days");

    if (diff === 0) {
      return (
        <>
          <i className="paperflite-calendar" />{" "}
          <span className={styles.name}>due today</span>
        </>
      )
    }

    if (diff > 0) {
      return (
      <>
        <i className="paperflite-calendar" />{" "}
        <span className={styles.name}>due {date.format("MMM D")}</span>
      </>
      )
    }

    // diff < 0 → overdue
    const abs = Math.abs(diff);
    if (abs < 7) {
      return (
        <>
          <i className={cx("paperflite-danger",styles.danger)} />
          <span className={styles.name}>overdue by {abs} {pluralize("day", abs)}</span>
        </>
      );
    }
    // For >1 week
    const weeks = Math.floor(abs / 7);
    return (
      <>
        <i className={cx("paperflite-danger",styles.danger)} />
        <span className={styles.name}>overdue by {weeks} {pluralize("week", weeks)}</span>
      </>
    );
  };

  return (
    <div
      className={cx(styles.card, styles.podcastCard)}
      onClick={() => onClick(course)}
    >
      <div className={styles.header}>
        <div className={cx(styles.top)}>
          <div className={styles.banner}>
            <Thumbnail
              className={styles.icon}
              media={icon}
              defaultImage={defaultPodcast}
              width={600}
              position={position}
              crop={crop || undefined}
            />
            <i className={cx("paperflite-micro-phone", styles.microphone)} />
          </div>
          {dueDate && course.status != "COMPLETED" && (
            <div className={styles.info}>
              <div className={styles.duedate}>
                <>{getDueDate()}</>
              </div>
              <div className={styles.type}>
                <i className="paperflite-waveform" />
                <span className={styles.name}>{podcastType}</span>
              </div>
            </div>
          )}
          {!dueDate && (
            <div className={styles.type}>
              <i className="paperflite-waveform" />
              <span className={styles.name}>{podcastType}</span>
            </div>
          )}
        </div>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span className={styles.value}>
              {FileService.formatDuration(duration)}
            </span>
          </div>
        </div>
        <div className={styles.details}>
          <Text className={styles.name} text={name} lines={2} />
        </div>
      </div>
      <div className={styles.footer}>
        {course.status === "NOT_STARTED" && (
          <div>
            <div className={styles.metadata}>
              <Thumbnail
                className={styles.icon}
                media={createdBy}
                defaultImage={defaultPodcast}
                width={18}
              />
              <span className={styles.days}>Added on {createdDateValue}</span>
            </div>
            <button className={cx(styles.action)}>
              <i className="paperflite-right-arrow" />
              <span>start learning</span>
            </button>
          </div>
        )}
        {course.status === "IN_PROGRESS" && (
          <div>
            <div className={styles.progressmetadata}>
              <div className={styles.progressBar}>
                <ProgressBar now={percentCompleted} />

                <span className={styles.value}>{Math.round(percentCompleted) || 0} % </span>
              </div>
              <div>
                <span className={styles.lessonCount}> {FileService.formatTime(completedDuration)} / {FileService.formatTime(duration)}</span>
              </div>
            </div>
            <button className={cx(styles.action, styles.start)}>
              <i className="paperflite-right-arrow" />
              <span>continue learning</span>
            </button>
          </div>
        )}
        {course.status === "COMPLETED" && (
          <div className={styles.completedWrapper} >
            <div className={styles.metadata}>
              <div className={styles.completed}>
                <i className="paperflite-calendar" />{" "}
                <span>completed on {completedDate || ""}</span>
              </div>
            </div>
            <button className={cx(styles.action)}>
              <i className="paperflite-right-arrow" />
              <span>view reports</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

interface SimulationCardProps {
  course: Simulation;
  onClick: (course: any) => void;
  status?: String;
}

export const SimulationCard: React.FC<SimulationCardProps> = ({
  course,
  onClick,
}) => {
  const name = useMemo(() => _.get(course, "course.name", ""), [course]);
  const description = useMemo(
    () => _.get(course, "course.description"),
    [course],
  );
  const icon = useMemo(
    () => _.get(course, "course.banner.background"),
    [course],
  );

  const position = useMemo(
    () => _.get(course, "course.banner.position", "center"),
    [course],
  );

  const crop = useMemo(
    () => _.get(course,"course.banner.crop", null),
    [course],
  );

  const score = useMemo(() => {
    const score = _.get(course, "score") || 0;
    return (score * 100).toFixed(0);
  }, [course]);
  const passPercentage = useMemo(
    () => _.get(course, "course.passPercentage", 0),
    [course],
  );

  const createdBy = useMemo(
    () => _.get(course, "course.createdBy.icon", null),
    [course],
  );

  const callType = useMemo(
    () => _.get(course, "course.callType", ""),
    [course],
  );

  const getCallType = useMemo(() => {
    return CALL_TYPES.find((item) => item.id === callType)?.title || "";
  }, [callType]);
  
  const industry = useMemo(
    () => _.get(course, "course.organization.industry", ""),
    [course],
  );

  const duration = useMemo(() => _.get(course, "course.duration"), [course]);

  const completedDateValue = useMemo(
    () => _.get(course, "completedDate"),
    [course],
  );
  const completedDate = completedDateValue
    ? moment(completedDateValue).format("D MMM, YYYY")
    : "";

  const getCourseCreated = () => {
    const created = moment(_.get(course, "createdDate"));
    const today = moment();
  
    const diffInDays = today.diff(created, "days");
  
    if (diffInDays === 0) return "today";
    if (diffInDays === 1) return "1 day ago";
    if (diffInDays < 7) return `${diffInDays} days ago`;
  
    return created.format("MMM D, YYYY");
  };

  const dueDate = _.get(course, "dueDate", null);
  const getDueDate = () => {
    if (!dueDate) return null; 

    const date = moment(dueDate).startOf("day");
    const today = moment().startOf("day");
    const diff = date.diff(today, "days");

    if (diff === 0) {
      return (
        <>
          <i className="paperflite-calendar" />{" "}
          <span className={styles.name}>due today</span>
        </>
      )
    }

    if (diff > 0) {
      return (
      <>
        <i className="paperflite-calendar" />{" "}
        <span className={styles.name}>due {date.format("MMM D")}</span>
      </>
      )
    }

    // diff < 0 → overdue
    const abs = Math.abs(diff);
    if (abs < 7) {
      return (
        <>
          <i className={cx("paperflite-danger",styles.danger)} />
          <span className={styles.name}>overdue by {abs} {pluralize("day", abs)}</span>
        </>
      );
    }
    // For >1 week
    const weeks = Math.floor(abs / 7);
    return (
      <>
        <i className={cx("paperflite-danger",styles.danger)} />
        <span className={styles.name}>overdue by {weeks} {pluralize("week", weeks)}</span>
      </>
    );
  };

  return (
    <div
      className={cx(styles.card, styles.simulationCard)}
      onClick={() => onClick(course)}
    >
      <div className={styles.header}>
        <div className={cx(styles.top)}>
          <div className={styles.banner}>
            <Thumbnail
              className={cx(styles.icon, {
                [styles.discovery]: callType === "discovery",
                [styles.coldcall]: callType === "cold_call",
              })}
              media={icon}
              defaultImage={defaultSimulation}
              width={600}
              position={position}
              crop={crop || undefined}
            />
            <div className={styles.phone}>
              <i className={cx("paperflite-phone",{
                [styles.discovery]: callType === "discovery",
                [styles.coldcall]: callType === "cold_call",
              })} />
            </div>
          </div>

          {dueDate && course.status != "COMPLETED" && (
            <div className={styles.info}>
              <div className={styles.duedate}>
                <>{getDueDate()}</>
              </div>
              <div className={styles.type}>
                <i className="paperflite-phone-call" />
                <span className={styles.name}>
                  {getCallType}
                </span>
              </div>
            </div>
          )}
          {!dueDate && (
            <div className={styles.type}>
              <i className="paperflite-phone-call" />
              <span className={styles.name}>{getCallType}</span>
            </div>
          )}
        </div>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span className={styles.value}>
              {duration} m
            </span>
          </div>
          {industry && <div className={styles.divider}></div>}
          {industry && (
            <div className={styles.metadata}>
              <i className="paperflite-industry"></i>
              <Text text={industry} className={styles.industry} lines={1} />
              {/* <span className={styles.value}>{industry}</span> */}
            </div>
          )}
        </div>
        <div className={styles.details}>
          <Text className={styles.name} text={name} lines={2} />
        </div>
      </div>

      <div className={styles.footer}>
        {(course.status === "NOT_STARTED" || course.status === "IN_PROGRESS") && (
            <div>
              <div className={styles.metadata}>
                <Thumbnail
                  className={styles.icon}
                  media={createdBy}
                  defaultImage={defaultSimulation}
                  width={18}
                />
                <span>
                  by {_.get(course, "course.createdBy.firstName", "")},{" "}
                  {getCourseCreated()}
                </span>
              </div>
              <button className={cx(styles.action)}>
                <i className="paperflite-right-arrow" />
                <span>open simulation</span>
              </button>
            </div>
          )}
        {course.status === "COMPLETED" && (
          <div>
            <div className={styles.metadata}>
              <span>
                score:{" "}
                <span
                  className={`
                    ${Number(score) >= passPercentage * 100 ? styles.pass : styles.fail}
                    ${Number(score) === 0 ? styles.score : ""}
                  `} >
                  {" "}
                  {Number(score) === 0 ? "na" : `${score}%`}{" "}
                </span>{" "}
              </span>
              <div className={styles.divider}></div>
              <div className={styles.completed}>
                <i className="paperflite-calendar" />{" "}
                <span>completed on {completedDate || ""}</span>
              </div>
            </div>
            <button className={cx(styles.action)}>
              <i className="paperflite-right-arrow" />
              <span>view reports</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AssignedCourseCard: React.FC<CourseCardProps> = ({
  course,
  status,
  onClick,
}) => {
  const entityType = _.get(course, "course.entityType", null )
  return (
    <>
      {entityType === "course" && (
        <MyCourseCard course={course} onClick={() => onClick(course)} />
      )}
      {entityType === "podcast" && (
        <PodcastCard course={course} onClick={() => onClick(course)} />
      )}
      {entityType === "simulation" && (
        <SimulationCard course={course} onClick={() => onClick(course)} />
      )}
    </>
  );
};

const InProgressCourseCard: React.FC<CourseCardProps> = ({
  course,
  onClick,
}) => {
  const entityType = _.get(course, "course.entityType", null);
  return(
    <>
      {entityType === "course" && (
        <MyCourseCard course={course} onClick={() => onClick(course)} />
      )}
      {entityType === "podcast" && (
        <PodcastCard course={course} onClick={() => onClick(course)} />
      )}
      {entityType === "simulation" && (
        <SimulationCard course={course} onClick={() => onClick(course)} />
      )}
    </>
  )
};


const CompletedCourseCard: React.FC<CourseCardProps> = ({
  course,
  onClick,
}) => {
  const entityType = _.get(course, "course.entityType", null);
  return (
    <>
      {entityType === "course" && (
        <MyCourseCard course={course} onClick={() => onClick(course)} />
      )}
      {entityType === "podcast" && (
        <PodcastCard course={course} onClick={() => onClick(course)} />
      )}
      {entityType === "simulation" && (
        <SimulationCard course={course} onClick={() => onClick(course)} />
      )}
    </>
  )
};


const CourseCard: React.FC<CourseCardProps> = ({ course, status, onClick }) => {
  switch (status) {
    case "NOT_STARTED":
      return (
        <AssignedCourseCard course={course} status={status} onClick={onClick} />
      );
    case "IN_PROGRESS":
      return (
        <InProgressCourseCard
          course={course}
          status={status}
          onClick={onClick}
        />
      );
    case "COMPLETED":
      return (
        <CompletedCourseCard
          course={course}
          status={status}
          onClick={onClick}
        />
      );
    default:
      return null;
  }
};

const PlaceholderCourseCard: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.placeholder)}>
      <div className={styles.header}>
        <div className={cx(styles.icon, styles.block)}></div>
      </div>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)}></div>
        <p className={cx(styles.description)}>
          <div className={styles.block}></div>
        </p>
      </div>
      <div className={styles.footer}>
        <div className={styles.block}></div>
      </div>
    </div>
  );
};

export { CourseCard, PlaceholderCourseCard };
